import { produce } from 'immer';
import { EMPTY, switchMap, tap } from 'rxjs';
import { Injectable } from '@angular/core';
import { Action, State, StateContext, StateToken, Store } from '@ngxs/store';
import _ from 'lodash';

import {
  BaseDiner,
  DinerType,
  LocationType,
  NormalizedDiner,
  NormalizedObject
} from 'src/app/models';
import { DinerService } from 'src/app/services';
import { GetMenuTally } from '../menu/menu.action';
import {
  AddDinerToTicket,
  AddDinerToTicketSuccess,
  AddTicketMealPlan
} from '../tickets/tickets.action';
import {
  AssignMealPlan,
  ClearSearchDinersResult,
  GetAssignedResidentsSuccess,
  GetDinerByBarcode,
  GetDinerByBarcodeFailed,
  GetDinerDetail,
  GetDinerDetailSuccess,
  GetDiners,
  GetSearchingDinersSuccess,
  MultipleDinersFound,
  SelectDiner,
  ShowMealPlanPrompt,
  UpdateChargeAccountBalance,
  UpdateDinerSuccess
} from './diners.action';
import { NormalizrHelper } from 'src/app/helpers';
import {
  LogRocketProvider,
  LOG_ROCKET_CUSTOM_EVENTS
} from 'src/app/providers/logrocket.provider';
import { AddToPendingDiners } from '../host-mode/host-mode.action';
import { TICKETS_STATE_TOKEN } from 'src/app/store/tickets/tickets.state';
import { AddReservationDiner } from '../reservation-control/reservation-control.action';
import { UpdateSelectedMealId } from 'src/app/store/location/location.action';
import { SelectNewSeat } from 'src/app/store/seats/seats.action';
import { SEATS_STATE_TOKEN } from 'src/app/store/seats/seats.state';
import { LOCATION_STATE_TOKEN } from 'src/app/store/location/location.state';
import { LocationStateHelper } from 'src/app/store/location/location.state.helper';

export interface DinersStateModel {
  diners: NormalizedObject<NormalizedDiner, number>;
  filteredDiners: BaseDiner[];
  assignedResidents: BaseDiner[];
}

export const DINERS_STATE_TOKEN = new StateToken<DinersStateModel>('diners');

@State({
  name: DINERS_STATE_TOKEN,
  defaults: {
    diners: {
      byId: null,
      allIds: []
    },
    filteredDiners: [],
    assignedResidents: []
  }
})
@Injectable()
export class DinersState {
  constructor(
    private readonly dinerService: DinerService,
    private readonly store: Store,
    private readonly logRocketProvider: LogRocketProvider
  ) {}

  @Action(SelectDiner)
  selectDiner(
    ctx: StateContext<DinersStateModel>,
    { ticket, addMealPlan, getDinerDetail, forceApplyMealPlan }: SelectDiner
  ) {
    const dinerId = ticket?.diner_id || ticket?.guest_of_diner_id;

    this.logRocketProvider.track(LOG_ROCKET_CUSTOM_EVENTS.Diner.Assigned, {
      device_ticket_uuid: ticket?.device_ticket_uuid,
      diner_name: ticket?.diner_name,
      guest_of_diner_id: ticket?.guest_of_diner_id,
      diner_id: ticket?.diner_id
    });

    if (dinerId) {
      const { diners } = ctx.getState();
      if (diners.allIds.indexOf(dinerId) === -1 || getDinerDetail) {
        ctx.dispatch(
          new GetDinerDetail(ticket, addMealPlan, forceApplyMealPlan)
        );
      } else if (addMealPlan) {
        ctx.dispatch(
          new AssignMealPlan(
            diners.byId[dinerId],
            ticket.device_ticket_uuid,
            forceApplyMealPlan
          )
        );
      }
    }
  }

  @Action(GetDiners, { cancelUncompleted: true })
  getDiners(
    { dispatch }: StateContext<DinersStateModel>,
    { params }: GetDiners
  ) {
    return this.dinerService
      .getDiners(params)
      .pipe(
        switchMap((diners) => dispatch(new GetSearchingDinersSuccess(diners)))
      );
  }

  @Action(GetSearchingDinersSuccess)
  getSearchingDinersSuccess(
    { setState }: StateContext<DinersStateModel>,
    { diners }: GetSearchingDinersSuccess
  ) {
    setState(
      produce((draft: DinersStateModel) => {
        draft.filteredDiners = diners;
      })
    );
  }

  @Action(GetDinerByBarcode)
  getDinerByBarcode(
    ctx: StateContext<DinersStateModel>,
    { barcode, searchType }: GetDinerByBarcode
  ) {
    return this.dinerService.getDinerByBarcode(barcode).pipe(
      switchMap((diners) => {
        // If no diners are found, dispatch an error
        if (!diners || diners.length === 0) {
          return ctx.dispatch(new GetDinerByBarcodeFailed(barcode));
        }
        // If only one diner is found
        if (diners.length === 1) {
          // If the search is for host mode, dispatch an action to add it to the pending diners
          const diner = diners[0];

          if (searchType === 'hostMode') {
            return ctx.dispatch(new AddToPendingDiners(diner));
          } else if (searchType === 'ticketAssignment') {
            const device_ticket_uuid =
              this.store.selectSnapshot(TICKETS_STATE_TOKEN).selectedTicket
                .data;

            return device_ticket_uuid
              ? ctx.dispatch(
                  new AddDinerToTicket({
                    diner,
                    device_ticket_uuid
                  })
                )
              : EMPTY;
          } else if (searchType === 'reservation') {
            return ctx.dispatch(new AddReservationDiner(diner));
          }
        }

        // If more than one diner is found, dispatch an action to show the list of diners
        return ctx.dispatch(new MultipleDinersFound(diners, barcode));
      })
    );
  }

  // Retrieves the diner detail after assigning it to a Ticket or selecting an existing ticket with diner
  // After updating the local store, if the diner has a valid meal plan it will add it or ask if it needs to be added based on the location configuration
  //If diner id is null, checks the guest of diner id to retrieve diner details
  @Action(GetDinerDetail)
  getDinerDetail(
    { dispatch }: StateContext<DinersStateModel>,
    { ticket, addMealPlan, forceApplyMealPlan }: GetDinerDetail
  ) {
    if (ticket) {
      const { diner_id, guest_of_diner_id, device_ticket_uuid } = ticket;
      const dinerId = diner_id || guest_of_diner_id;

      return this.dinerService
        .getDinerDetail(dinerId)
        .pipe(
          tap((diner) =>
            dispatch(
              new GetDinerDetailSuccess(
                diner,
                addMealPlan,
                forceApplyMealPlan,
                device_ticket_uuid
              )
            )
          )
        );
    }
  }

  @Action(GetDinerDetailSuccess)
  getDinerDetailSuccess(
    { dispatch }: StateContext<DinersStateModel>,
    {
      diner,
      addMealPlan,
      forceApplyMealPlan,
      device_ticket_uuid
    }: GetDinerDetailSuccess
  ) {
    const location = this.store.selectSnapshot(LOCATION_STATE_TOKEN).location;
    const locationType = location.type;
    const isQuickService: boolean = locationType === LocationType.QuickService;

    const isSelectMenuTally =
      !!diner.id &&
      LocationStateHelper.getIsPersonalizedResidentMenus(location) &&
      diner.type === DinerType.Resident;

    dispatch(new UpdateDinerSuccess(diner));

    if (isSelectMenuTally) {
      const ticketsState = this.store.selectSnapshot(TICKETS_STATE_TOKEN);
      const selectedTicketId = ticketsState.selectedTicket.data;
      const ticketsById = ticketsState.tickets.byId;

      const createdTicket =
        !selectedTicketId || !ticketsById
          ? null
          : ticketsById[selectedTicketId];

      if (createdTicket) {
        this.store.dispatch(
          new GetMenuTally(
            diner.id,
            createdTicket.meal_id,
            createdTicket.ticket_date
          )
        );
      }
    }

    if (isQuickService) {
      dispatch(
        // diner, false, false, forceApplyMealPlan
        new AddDinerToTicketSuccess({
          diner,
          device_ticket_uuid,
          addMealPlan: false,
          getDinerDetail: false,
          forceApplyMealPlan
        })
      );
    }

    if (addMealPlan) {
      dispatch(
        new AssignMealPlan(diner, device_ticket_uuid, forceApplyMealPlan)
      );
    }
  }

  @Action(AssignMealPlan)
  assignMealPlan(
    ctx: StateContext<DinersStateModel>,
    { diner, device_ticket_uuid, forceApplyMealPlan }: AssignMealPlan
  ) {
    if (device_ticket_uuid && diner.meal_plan) {
      this.logRocketProvider.track(
        LOG_ROCKET_CUSTOM_EVENTS.Diner.OptForMealPlan,
        {
          device_ticket_uuid,
          mealsuite_id: diner.mealsuite_id,
          diner_id: diner.id
        }
      );
      const location = this.store.selectSnapshot(LOCATION_STATE_TOKEN).location;
      const locationType = location.type;
      const isSelfService: boolean = locationType === LocationType.SelfServe;
      const autoChooseMealPlan = isSelfService
        ? false
        : location.auto_choose_mealplan_when_available;
      let action = new ShowMealPlanPrompt();
      if (forceApplyMealPlan || autoChooseMealPlan) {
        action = new AddTicketMealPlan({
          mealPlanId: diner.meal_plan.id,
          ticketUuid: device_ticket_uuid
        });
      }
      return ctx.dispatch(action);
    }
  }

  @Action(GetAssignedResidentsSuccess)
  getAssignedResidentsSuccess(
    ctx: StateContext<DinersStateModel>,
    { residents }: GetAssignedResidentsSuccess
  ) {
    ctx.patchState({ assignedResidents: residents });
  }

  @Action(ClearSearchDinersResult)
  clearSearchDinersResult(ctx: StateContext<DinersStateModel>) {
    ctx.patchState({ filteredDiners: [] });
  }

  @Action(UpdateChargeAccountBalance)
  updateChargeAccountBalance(
    ctx: StateContext<DinersStateModel>,
    { dinerId, transaction }: UpdateChargeAccountBalance
  ) {
    ctx.setState(
      produce((draft) => {
        const diner = draft.diners.byId[dinerId];
        const chargeAccount = diner?.charge_accounts.find(
          (i) => i.id === transaction.pos_charge_account_id
        );
        if (chargeAccount) {
          const newValue = chargeAccount.available_credit - transaction.amount;
          chargeAccount.available_credit = newValue > 0 ? newValue : 0;
        }
      })
    );
  }

  //Get new tally from dropdown selector
  @Action(UpdateSelectedMealId)
  getNewMenuTallyOnMealChange(
    ctx: StateContext<DinersStateModel>,
    { meal_id, date }: UpdateSelectedMealId
  ) {
    const { tickets, selectedTicket: selectedTicketId } =
      this.store.selectSnapshot(TICKETS_STATE_TOKEN);
    const ticketsById = tickets.byId;
    const selectedTicket = ticketsById && ticketsById[selectedTicketId.data];
    const selectedDinerId: number = selectedTicket?.diner_id;
    const isPersonalizedResidentMenus =
      LocationStateHelper.getIsPersonalizedResidentMenus(
        this.store.selectSnapshot(LOCATION_STATE_TOKEN).location
      );
    const dinersById = ctx.getState().diners.byId;

    if (!isPersonalizedResidentMenus || !selectedDinerId) {
      return;
    }

    const diner = dinersById && dinersById[selectedDinerId];

    if (!diner || (diner.id && diner.type !== DinerType.Resident)) {
      return;
    }

    ctx.dispatch(new GetMenuTally(diner.id, meal_id, date));
  }

  @Action(SelectNewSeat)
  addDinerToNewSeat(
    ctx: StateContext<DinersStateModel>,
    payload: SelectNewSeat
  ) {
    const selectedSeat =
      this.store.selectSnapshot(SEATS_STATE_TOKEN).seats.byId[payload.seatId];
    const device_ticket_uuid = selectedSeat.device_ticket_uuid;

    if (!device_ticket_uuid) {
      return;
    }

    const { selectedTicket, tickets } =
      this.store.selectSnapshot(TICKETS_STATE_TOKEN);
    const ticket = tickets.byId[device_ticket_uuid];

    if (!ticket) {
      return;
    }
    const { diner_id, guest_of_diner_id } = ticket;

    const ticketDinerId = diner_id || guest_of_diner_id;

    if (!ticketDinerId) {
      return;
    }

    const dinerState = ctx.getState();
    const isDinerLoaded = dinerState.diners.allIds.includes(ticketDinerId);
    const selectedDinerId: number =
      tickets.byId &&
      selectedTicket?.data &&
      tickets.byId[selectedTicket.data].diner_id;

    if (isDinerLoaded && selectedDinerId === ticketDinerId) {
      return;
    }
    const selectedDiner =
      dinerState.diners.byId && dinerState.diners.byId[ticketDinerId];

    if (!selectedDiner) {
      return this.dinerService.getDinerDetail(ticketDinerId).pipe(
        tap((diner) => {
          ctx.setState(
            produce((draft: DinersStateModel) => {
              const dinerEntities = NormalizrHelper.normalizeDiners([diner])
                .entities.diners;
              draft.diners = {
                byId: {
                  ...draft.diners.byId,
                  ...dinerEntities
                },
                allIds: _.union(draft.diners.allIds, [ticketDinerId])
              };
            })
          );

          const isPersonalizedResidentMenus =
            LocationStateHelper.getIsPersonalizedResidentMenus(
              this.store.selectSnapshot(LOCATION_STATE_TOKEN).location
            );

          const isSelectMenuTally =
            !!diner_id &&
            diner.type === DinerType.Resident &&
            isPersonalizedResidentMenus;

          if (isSelectMenuTally) {
            ctx.dispatch(
              new GetMenuTally(diner.id, ticket.meal_id, ticket.ticket_date)
            );
          }
        })
      );
    }
  }

  @Action(UpdateDinerSuccess)
  updateDinerSuccess(
    ctx: StateContext<DinersStateModel>,
    { diner }: UpdateDinerSuccess
  ) {
    ctx.setState(
      produce((draft) => {
        const dinerEntities = NormalizrHelper.normalizeDiners([diner]).entities
          .diners;
        draft.diners = {
          byId: {
            ...draft.diners.byId,
            ...dinerEntities
          },
          allIds: _.union(draft.diners.allIds, [diner.id])
        };
      })
    );
  }
}
